import React from 'react';
import PropTypes from 'prop-types';

/**
 * SidebarBranding - Reusable component for sidebar brand display
 * Layout: Logo (top) -> Company Name -> Tagline -> HaliTrade/HaliAgent -> Badge
 */
const SidebarBranding = ({
  logoSrc,
  companyName,
  tagline = '',
  platformName = 'HaliTrade', // 'HaliTrade' or 'HaliAgent'
  viewBadge = 'Platform View',
  primaryColor = '#DC2626',
  darkMode = true,
  collapsed = false
}) => {
  // Colors adapted for visibility on any background
  const textColor = darkMode ? '#FFFFFF' : '#1F2937';
  const subtitleColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6B7280';
  const brandHighlightColor = platformName === 'HaliFund' ? primaryColor : '#DC2626';
  const brandBaseColor = darkMode ? 'rgba(255,255,255,0.9)' : '#374151';
  const poweredByColor = darkMode ? 'rgba(255,255,255,0.5)' : '#9CA3AF';

  // Get the second part of platform name (Trade, Agent, etc.)
  const getPlatformSuffix = () => {
    if (platformName === 'HaliTrade' || platformName === 'HaliFlow') return 'TRADE';
    if (platformName === 'HaliAgent') return 'AGENT';
    if (platformName === 'HaliVault') return 'VAULT';
    if (platformName === 'HaliLab') return 'LAB';
    if (platformName === 'HaliFund') return 'FUND';
    return 'TRADE';
  };

  if (collapsed) {
    // Collapsed view - just logo centered
    return (
      <div className="flex flex-col items-center py-4">
        <img
          src={logoSrc}
          alt={companyName || platformName}
          style={{
            width: '40px',
            height: '40px',
            objectFit: 'contain',
            borderRadius: '10px',
            boxShadow: darkMode
              ? '0 3px 10px rgba(220,38,38,0.3)'
              : '0 3px 10px rgba(220,38,38,0.2)'
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center py-2 px-2 w-full">
      {/* Logo - Compact */}
      <div
        className="flex items-center justify-center overflow-hidden mb-2"
        style={{
          width: '48px',
          height: '48px',
        }}
      >
        <img
          src={logoSrc}
          alt={companyName || platformName}
          style={{
            width: '48px',
            height: '48px',
            objectFit: 'contain',
            borderRadius: '12px',
            boxShadow: darkMode
              ? '0 4px 14px rgba(220,38,38,0.3)'
              : '0 4px 14px rgba(220,38,38,0.2)'
          }}
        />
      </div>

      {/* Company Name - Compact */}
      {companyName && (
        <h1
          className="text-xs font-bold uppercase tracking-wide leading-tight text-center w-full px-1"
          style={{ color: textColor }}
          title={companyName}
        >
          {companyName}
        </h1>
      )}

      {/* Tagline */}
      {tagline && (
        <p
          className="text-[0.55rem] uppercase tracking-wider mt-0.5 px-1 text-center"
          style={{ color: subtitleColor }}
          title={tagline}
        >
          {tagline}
        </p>
      )}

      {/* Divider */}
      <div
        className="w-8 h-px my-1.5"
        style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }}
      />

      {/* HaliTrade / HaliAgent branding */}
      <div className="flex flex-col items-center">
        <div className="flex items-center gap-0.5">
          <span
            className="text-[0.65rem] uppercase tracking-[0.1em] font-semibold"
            style={{ color: brandBaseColor }}
          >
            HALI
          </span>
          <span
            className="text-[0.65rem] uppercase tracking-[0.1em] font-bold"
            style={{ color: brandHighlightColor }}
          >
            {getPlatformSuffix()}
          </span>
        </div>
        <span
          className="text-[0.5rem] tracking-wide"
          style={{ color: poweredByColor }}
        >
          powered by HaliSoft
        </span>
      </div>

      {/* View Badge */}
      {viewBadge && (
        <span
          className="mt-2 inline-flex px-2 py-0.5 text-[0.5rem] font-bold uppercase tracking-wider rounded-full"
          style={{
            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            color: darkMode ? 'rgba(255,255,255,0.7)' : '#6B7280',
            border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)'
          }}
        >
          {viewBadge}
        </span>
      )}
    </div>
  );
};

SidebarBranding.propTypes = {
  logoSrc: PropTypes.string.isRequired,
  companyName: PropTypes.string,
  tagline: PropTypes.string,
  platformName: PropTypes.oneOf(['HaliTrade', 'HaliAgent', 'HaliVault', 'HaliLab', 'HaliFlow', 'HaliFund']),
  viewBadge: PropTypes.string,
  primaryColor: PropTypes.string,
  darkMode: PropTypes.bool,
  collapsed: PropTypes.bool
};

export default SidebarBranding;
